import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, gcd, roundTo, toLatex } from '@yuan-math/math';
import { choiceExercise, fractionExercise, numericExercise, options, template, trap } from './authoring.ts';

/** Ratio, rate and percentage exercises. */
export const RATIO_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'ratio-simplify',
    conceptId: 'ratios',
    description: 'Simplify a ratio to its lowest terms.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const a = rng.int(1, difficulty === 2 ? 6 : 9);
      const b = rng.int(1, difficulty === 2 ? 6 : 9);
      const factor = rng.int(2, difficulty === 2 ? 5 : 9);
      const first = a * factor;
      const second = b * factor;
      const divisor = gcd(first, second);
      return numericExercise({
        prompt: `Simplify the ratio ${first} : ${second}. What is the first number in the simplified ratio?`,
        latex: `${first} : ${second}`,
        value: first / divisor,
        summary: `Divide both parts by ${divisor}: ${first / divisor} : ${second / divisor}.`,
        hint: `What is the largest number that divides both ${first} and ${second}?`,
        steps: [
          { label: 'Find the greatest common factor.', latex: `\\gcd(${first}, ${second}) = ${divisor}` },
          { label: 'Divide both parts by it.', latex: `${first / divisor} : ${second / divisor}` },
        ],
        traps: [trap(second / divisor, 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'ratio-part-of-whole',
    conceptId: 'ratios',
    description: 'Read a part-to-whole fraction from a part-to-part ratio.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 7);
      const b = rng.int(1, 7);
      const total = a + b;
      const items = rng.pick([
        { first: 'red', second: 'blue', noun: 'beads' },
        { first: 'girls', second: 'boys', noun: 'children' },
        { first: 'cats', second: 'dogs', noun: 'animals' },
      ]);
      return fractionExercise({
        prompt: `The ratio of ${items.first} to ${items.second} is ${a} : ${b}. What fraction of the ${items.noun} are ${items.first}?`,
        latex: `${a} : ${b}`,
        value: fraction(a, total),
        requireSimplified: true,
        summary: `There are ${a} + ${b} = ${total} parts altogether, and ${a} of them are ${items.first}.`,
        hint: 'Add the two parts of the ratio to find how many parts there are in total.',
        steps: [
          { label: 'Add the parts to find the whole.', latex: `${a} + ${b} = ${total}` },
          { label: 'Write the first quantity over the total.', latex: toLatex(fraction(a, total)) },
        ],
        // Reading a part-to-part ratio as if it were a fraction of the whole.
        traps: [{ response: `${a}/${b}`, misconceptionId: 'ratio-part-as-whole' }],
      });
    },
  }),

  template({
    id: 'ratio-scale-up',
    conceptId: 'ratios',
    description: 'Scale a ratio to find a missing quantity.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, 6);
      const b = rng.int(2, 6);
      const factor = rng.int(2, difficulty === 4 ? 12 : 6);
      const known = a * factor;
      const answer = b * factor;
      return numericExercise({
        prompt: `A recipe uses flour and sugar in the ratio ${a} : ${b}. If ${known} cups of flour are used, how much sugar is needed?`,
        latex: `${a} : ${b} = ${known} : ?`,
        value: answer,
        summary: `The flour was multiplied by ${factor}, so the sugar must be too: ${b} × ${factor} = ${answer}.`,
        hint: `What do you multiply ${a} by to get ${known}?`,
        steps: [
          { label: 'Find the scale factor.', latex: `${known} \\div ${a} = ${factor}` },
          { label: 'Apply it to the other part.', latex: `${b} \\times ${factor} = ${answer}` },
        ],
        traps: [trap(known - a + b, 'ratio-part-as-whole')],
      });
    },
  }),

  template({
    id: 'unit-rate-compute',
    conceptId: 'unit-rates',
    description: 'Find a unit rate by dividing.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const rate = rng.int(2, difficulty === 2 ? 20 : 65);
      const units = rng.int(2, difficulty === 2 ? 8 : 12);
      const total = rate * units;
      const scenario = rng.pick([
        { prompt: `A car travels ${total} miles in ${units} hours. What is its speed in miles per hour?`, unit: 'miles per hour' },
        { prompt: `${total} words are typed in ${units} minutes. What is the rate in words per minute?`, unit: 'words per minute' },
        { prompt: `${units} kg of rice costs £${total}. What is the price per kilogram?`, unit: 'per kg' },
      ]);
      return numericExercise({
        prompt: scenario.prompt,
        value: rate,
        unit: scenario.unit,
        summary: `${total} ÷ ${units} = ${rate} ${scenario.unit}.`,
        hint: '"Per" means divide — and it tells you which number goes first.',
        steps: [
          { label: 'A unit rate divides by the second quantity.', latex: `${total} \\div ${units}` },
          { label: 'Compute.', latex: String(rate) },
        ],
        traps: [trap(roundTo(units / total, 4), 'rate-inverted')],
      });
    },
  }),

  template({
    id: 'unit-rate-compare',
    conceptId: 'unit-rates',
    description: 'Compare two offers using unit rates.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const unitA = rng.int(2, 9);
      const unitB = unitA + rng.int(1, 4);
      const qtyA = rng.int(2, 8);
      const qtyB = rng.int(2, 8);
      const totalA = unitA * qtyA;
      const totalB = unitB * qtyB;
      // Lower price per unit is the better buy; unitA is always the lower.
      const { choices, correctId } = options(
        [
          { label: `${qtyA} kg for £${totalA}`, correct: true },
          { label: `${qtyB} kg for £${totalB}`, misconceptionId: 'rate-inverted' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'Which is the better value?',
        choices,
        correctId,
        summary: `£${totalA} ÷ ${qtyA} = £${unitA} per kg, against £${totalB} ÷ ${qtyB} = £${unitB} per kg.`,
        hint: 'Reduce both to the price of one kilogram, then compare.',
        steps: [
          { label: 'First offer, per kilogram.', latex: `${totalA} \\div ${qtyA} = ${unitA}` },
          { label: 'Second offer, per kilogram.', latex: `${totalB} \\div ${qtyB} = ${unitB}` },
          { label: 'Lower price per kilogram wins.', latex: `${unitA} < ${unitB}` },
        ],
      });
    },
  }),

  template({
    id: 'percent-of',
    conceptId: 'percentages',
    description: 'Find a percentage of a quantity.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const percent = rng.pick(difficulty === 2 ? [10, 25, 50, 75] : difficulty === 3 ? [5, 15, 20, 30, 40, 60] : [12, 18, 35, 45, 65, 85]);
      const base = rng.int(2, difficulty === 4 ? 40 : 20) * 20;
      const value = roundTo((percent / 100) * base, 2);
      return numericExercise({
        prompt: `What is ${percent}% of ${base}?`,
        latex: `${percent}\\% \\times ${base}`,
        value,
        summary: `${percent}% is ${percent / 100}, and ${percent / 100} × ${base} = ${value}.`,
        hint: 'Convert the percentage to a decimal first, then multiply.',
        steps: [
          { label: 'Convert the percentage.', latex: `${percent}\\% = ${percent / 100}` },
          { label: '"Of" means multiply.', latex: `${percent / 100} \\times ${base} = ${value}` },
        ],
        traps: [trap(percent * base, 'percent-not-converted')],
      });
    },
  }),

  template({
    id: 'percent-discount',
    conceptId: 'percentages',
    description: 'Apply a percentage discount.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const percent = rng.pick([10, 15, 20, 25, 30, 40]);
      const price = rng.int(2, 20) * 20;
      const discount = roundTo((percent / 100) * price, 2);
      const value = roundTo(price - discount, 2);
      return numericExercise({
        prompt: `A jacket costs £${price} and is reduced by ${percent}%. What do you pay?`,
        value,
        unit: '£',
        summary: `The discount is £${discount}, so you pay £${price} − £${discount} = £${value}.`,
        hint: `You pay ${100 - percent}% of the original price.`,
        steps: [
          { label: 'Find the discount.', latex: `${percent / 100} \\times ${price} = ${discount}` },
          { label: 'Subtract it from the price.', latex: `${price} - ${discount} = ${value}` },
          { label: 'Or in one step.', latex: `${(100 - percent) / 100} \\times ${price} = ${value}` },
        ],
        traps: [trap(discount, 'percent-not-converted')],
      });
    },
  }),

  template({
    id: 'percent-conversion',
    conceptId: 'percentages',
    description: 'Convert between a percentage, a fraction and a decimal.',
    difficulties: [1, 2],
    build: ({ rng }) => {
      const percent = rng.pick([10, 20, 25, 40, 50, 60, 75, 80]);
      const asDecimal = percent / 100;
      return numericExercise({
        prompt: `Write ${percent}% as a decimal.`,
        value: asDecimal,
        summary: `${percent}% means ${percent} hundredths, which is ${asDecimal}.`,
        hint: 'A percentage is a number of hundredths — divide by 100.',
        steps: [
          { label: 'Percent means "per hundred".', latex: `\\frac{${percent}}{100}` },
          { label: 'As a decimal.', latex: String(asDecimal) },
        ],
        traps: [trap(percent, 'percent-not-converted')],
      });
    },
  }),
];
